import { createPublicPromise } from './utils'
import { Buffer } from './buffer'

const MediaSource = window.MediaSource || window.WebKitMediaSource

/** @enum {string} */
export const MSEErrorType = {
  CANCELLED: 'canceled',
  UPDATE_ERROR: 'updateError'
}

export class MSEError extends Error {
  /**
   * @param {MSEErrorType} type
   * @param {any} [msg]
   */
  constructor (type, msg) {
    super(msg || type)
    this.type = type
    this.msg = msg
  }
}

export class MSE {
  /** @type { HTMLMediaElement | null } */
  media = null

  /** @type { MediaSource | null } */
  mediaSource = null

  _openPromise = createPublicPromise()

  _queue = Object.create(null)

  _sourceBuffer = Object.create(null)

  /**
   * @param {HTMLMediaElement} [media]
   */
  constructor (media) {
    if (media) this.bindMedia(media)
  }

  get duration () {
    return this.mediaSource?.duration || -1
  }

  /**
   * @param { number } duration
   * @return { Promise }
  */
  updateDuration (duration) {
    return this._enqueueBlockingOp(() => {
      if (this.mediaSource) this.mediaSource.duration = duration
    })
  }

  /** @return { Promise } */
  open () {
    return this._openPromise
  }

  /**
   * @param { HTMLMediaElement } media
   * @return { Promise }
  */
  async bindMedia (media) {
    if (this.mediaSource || this.media) await this.unbindMedia()
    if (!media || !MediaSource) throw new Error('Param media or MediaSource does not exist')
    this.media = media
    const ms = this.mediaSource = new MediaSource()
    media.src = URL.createObjectURL(ms)

    const onOpen = () => {
      ms.removeEventListener('sourceopen', onOpen)
      URL.revokeObjectURL(media.src)
      this._openPromise.resolve()
    }

    ms.addEventListener('sourceopen', onOpen)

    return this._openPromise
  }

  /** @return { Promise } */
  async unbindMedia () {
    const ms = this.mediaSource

    if (ms) {
      Object.keys(this._queue).forEach((t) => {
        const queue = this._queue[t]
        if (queue) {
          queue.forEach(x => x.promise.reject(new MSEError(MSEErrorType.CANCELLED)))
        }
      })

      if (ms.readyState === 'open') {
        try {
          ms.endOfStream()
        } catch (error) {
          // ignore
        }
      }

      Object.keys(this._sourceBuffer).forEach(k => {
        try {
          ms.removeSourceBuffer(this._sourceBuffer[k])
        } catch (error) {
          // ignore
        }
      })
    }

    if (this.media) {
      this.media.removeAttribute('src')
      try {
        this.media.load()
      } catch (error) {
        // ignore
      }
      this.media = null
    }

    this.mediaSource = null
    this._openPromise = createPublicPromise()
    this._queue = Object.create(null)
    this._sourceBuffer = Object.create(null)
  }

  /**
   * @param { string } type
   * @param { string } mimeType
  */
  createSource (type, mimeType) {
    if (this._sourceBuffer[type] || !this.mediaSource) return
    const sb = this._sourceBuffer[type] = this.mediaSource.addSourceBuffer(mimeType)
    sb.mimeType = mimeType
    sb.addEventListener('updateend', this._onSBUpdateEnd.bind(this, type))
    sb.addEventListener('error', this._onSBUpdateError.bind(this, type))
  }

  /**
   * @param { string } type
   * @param { string } mimeType
   * @return { Promise }
  */
  changeType (type, mimeType) {
    const sb = this._sourceBuffer[type]
    if (!this.mediaSource || !sb || sb.mimeType === mimeType || typeof sb.changeType !== 'function') return Promise.resolve()

    return this._enqueueOp(() => {
      sb.changeType(mimeType)
    })
  }

  /**
   * @param { string } type
   * @param { string } mimeType
   * @return { Promise }
  */
  createOrChangeSource (type, mimeType) {
    this.createSource(type, mimeType)
    return this.changeType(type, mimeType)
  }

  /**
   * @param { string } type
   * @param { BufferSource } buffer
   * @return { Promise }
  */
  append (type, buffer) {
    if (!buffer || !buffer.byteLength) {
      return Promise.resolve()
    }
    return this._enqueueOp(type, () => {
      if (!this.mediaSource) return
      this._sourceBuffer[type].appendBuffer(buffer)
    })
  }

  /**
   * @param { string } type
   * @param { number } startTime
   * @param { number } endTime
   * @return { Promise }
  */
  remove (type, startTime, endTime) {
    return this._enqueueOp(type, () => {
      if (!this.mediaSource) return
      const sb = this._sourceBuffer[type]
      if (startTime >= endTime || !sb) {
        this._onSBUpdateEnd(type)
        return
      }

      sb.remove(startTime, endTime)
    })
  }

  clearAllBuffer () {
    let p
    Object.keys(this._sourceBuffer).forEach(k => {
      p = this._enqueueOp(k, () => {
        if (!this.mediaSource) return
        const sb = this._sourceBuffer[k]
        sb.remove(0, Buffer.end(Buffer.get(sb)))
      })
    })
    return p
  }

  /**
   * @param {EndOfStreamError} [reason]
   * @returns {Promise}
   */
  endOfStream (reason) {
    if (!this.mediaSource || this.mediaSource.readyState !== 'open') return Promise.resolve()
    return this._enqueueBlockingOp(() => {
      const ms = this.mediaSource
      if (!ms || ms.readyState !== 'open') return
      if (reason) {
        ms.endOfStream(reason)
      } else {
        ms.endOfStream()
      }
    })
  }

  setLiveSeekableRange (start, end) {
    const ms = this.mediaSource
    if (start < 0 || end < start || !ms?.setLiveSeekableRange || ms.readyState !== 'open') return
    ms.setLiveSeekableRange(start, end)
  }

  /**
   * @param { string } type
   * @return { TimeRanges | void }
  */
  buffered (type) {
    return Buffer.get(this._sourceBuffer[type])
  }

  /**
   * @param { string } type
   * @return { number }
  */
  bufferStart (type) {
    return Buffer.start(this.buffered(type))
  }

  /**
   * @param { string } type
   * @return { number }
  */
  bufferEnd (type) {
    return Buffer.end(this.buffered(type))
  }

  _enqueueOp (type, exec) {
    if (!this.mediaSource) return Promise.resolve()
    const queue = this._queue[type] = this._queue[type] || []
    const op = {
      exec,
      promise: createPublicPromise()
    }

    queue.push(op)

    if (queue.length === 1) {
      this._startQueue(type)
    }

    return op.promise
  }

  async _enqueueBlockingOp (exec) {
    if (!this.mediaSource) return Promise.resolve()
    const types = Object.keys(this._sourceBuffer)
    if (!types.length) {
      return exec()
    }

    const waiters = []
    types.forEach(t => {
      const queue = this._queue[t]
      const prom = createPublicPromise()
      waiters.push(prom)
      queue.push({ exec: () => prom.resolve(), promise: prom })
      if (queue.length === 1) {
        this._startQueue(t)
      }
    })

    return Promise.all(waiters).then(() => {
      try {
        return exec()
      } finally {
        types.forEach(t => {
          const queue = this._queue[t]
          const sb = this._sourceBuffer[t]
          queue.shift()
          if (!sb || !sb.updating) {
            this._startQueue(t)
          }
        })
      }
    })
  }

  _startQueue (type) {
    const queue = this._queue[type]
    if (queue) {
      const op = queue[0]
      if (op) {
        try {
          op.exec()
        } catch (error) {
          op.promise.reject(error)
          queue.shift()
          this._startQueue(type)
        }
      }
    }
  }

  _onSBUpdateEnd = (type) => {
    const queue = this._queue[type]
    if (queue) {
      const op = queue.shift()
      if (op) {
        op.promise.resolve()
        this._startQueue(type)
      }
    }
  }

  _onSBUpdateError = (type, event) => {
    const queue = this._queue[type]
    if (queue) {
      const op = queue[0]
      if (op) {
        op.promise.reject(new MSEError(MSEErrorType.UPDATE_ERROR, event))
        // Do not shift from queue, 'updateend' event will fire next
      }
    }
  }

  /**
   * @param {string} [mime='video/mp4; codecs="avc1.42E01E,mp4a.40.2"']
   * @returns {boolean}
   */
  static isSupported (mime = 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"') {
    if (!MediaSource) return false
    try {
      return MediaSource.isTypeSupported(mime)
    } catch (error) {
      return false
    }
  }
}
